import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  signal,
} from '@angular/core';
import { AppStyle } from 'src/app/models/app-style.type';
import { AppStyleService } from 'src/app/table/services/app-style-service';
import { FormatDatePipe } from '../../table/pipes/format-date.pipe';
import { OverlayDirective } from '../overlay/overlay.directive';
import { calendarChartDefaultColorPalette } from './models/calendar-chart-default-color-palette';
import {
  CalendarChartBar,
  CalendarChartInput,
  CalendarChartItem,
  CalendarChartItemV2,
  CalendarChartLegendItem,
} from './models/calendar-chart-input.interface';
import {
  calendarChartContainerHeightMap,
  calendarChartContainerHeightMapInTileRow,
  calendarChartMaxBarHeightMap,
  calendarChartMaxBarHeightMapInTileRow,
} from './models/calendar-chart-sizes';

@Component({
  selector: 'pozi-calendar-chart',
  imports: [CommonModule, OverlayDirective, FormatDatePipe],
  templateUrl: './calendar-chart.component.html',
  styleUrl: './calendar-chart.component.scss',
})
export class CalendarChartComponent {
  readonly defaultColors = calendarChartDefaultColorPalette;

  data: InputSignal<CalendarChartInput> = input.required<CalendarChartInput>();

  itemsWithEqualSize = input<boolean>(false);

  /** Shows the hour-based Y-axis column to the right of the bars when true. */
  yAxis = input<boolean>(false);

  appStyle = signal<AppStyle>('default');

  /**
   * Computed signal that returns calendar chart items in the `CalendarChartItemV2` format.
   *
   * - If the input item is already a `CalendarChartItemV2`, it is returned as-is.
   * - If the input item is a `CalendarChartItem`, it is transformed using
   *   `transformCalendarChartItemToV2`.
   *
   * @returns Array of `CalendarChartItemV2` objects, ready for rendering.
   */
  items = computed<CalendarChartItemV2[]>(() => {
    return this.data()
      .calendarChartItems.map(
        (item: CalendarChartItem | CalendarChartItemV2) =>
          'bars' in item ? item : this.transformCalendarChartItemToV2(item)
      )
      .slice(-28); // Temporary fix: limit to showing the last 28 days until design supports larger intervals
  });

  largestItemWidth = computed(() => {
    let maxBars = 0;

    for (const item of this.items()) {
      if (item.bars?.length > maxBars) {
        maxBars = item.bars.length;
      }
    }
    return maxBars * 8 + (maxBars - 1 * 4); // 8 is the bar width and 4 is the gap between them
  });

  /**
   * Converts an array of `CalendarChartLegendItem` objects into a `Map`
   * where each entry maps the item's `key` to its `color`.
   *
   * Items without a `key` are ignored.
   */
  legendColorMap = computed<Map<string, string>>(() => {
    return new Map(
      this.data()
        .legendItems?.filter(
          (legendItem: CalendarChartLegendItem) => !!legendItem.key
        )
        .map((legendItem: CalendarChartLegendItem) => [
          legendItem.key as string,
          legendItem.color,
        ])
    );
  });

  isInTileRow: InputSignal<boolean> = input<boolean>(false);

  containerHeight = computed<number>(
    () =>
      (this.isInTileRow()
        ? calendarChartContainerHeightMapInTileRow
        : calendarChartContainerHeightMap)[this.appStyle()]
  );

  maxBarHeightInPixels = computed<number>(
    () =>
      (this.isInTileRow()
        ? calendarChartMaxBarHeightMapInTileRow
        : calendarChartMaxBarHeightMap)[this.appStyle()]
  );

  /**
   * The maximum amount of time (in minutes) represented by any bar, found by
   * parsing the duration text (e.g. "8h 12m") from every bar's tooltip.
   * This is the bar that reaches the top of the Y-axis.
   */
  yAxisMaxMinutes = computed<number>(() => {
    let maxMinutes = 0;
    for (const item of this.items()) {
      for (const bar of item.bars) {
        const minutes = this.getBarDurationInMinutes(bar);
        if (minutes > maxMinutes) {
          maxMinutes = minutes;
        }
      }
    }
    return maxMinutes;
  });

  /** Top Y-axis label value: the highest bar's time rounded to the closest hour. */
  yAxisTopHours = computed<number>(() =>
    Math.round(this.yAxisMaxMinutes() / 60)
  );

  /** Middle Y-axis label value: the midpoint between the top and 0, rounded. */
  yAxisMiddleHours = computed<number>(() =>
    Math.round(this.yAxisTopHours() / 2)
  );

  cdkOverlayPositionPairs: ConnectedPosition[] = [
    {
      offsetX: 8,
      offsetY: 0,
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      panelClass: 'right',
    },
    {
      offsetX: -8,
      offsetY: 0,
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      panelClass: 'left',
    },
    {
      offsetX: 0,
      offsetY: 8,
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'bottom',
    },
    {
      offsetX: 0,
      offsetY: -8,
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      panelClass: 'top',
    },
  ];

  constructor() {
    const appStyleService = inject(AppStyleService);

    this.appStyle.set(appStyleService.getAppStyle());
  }

  /**
   * Returns the display label for a given calendar chart item.
   *
   * Priority:
   * 1. If the item has a `label`, return it.
   * 2. Otherwise, if the item has a `date`, return the day of the month extracted from the date.
   * 3. If neither is available, return an empty string.
   *
   * @param item - The `CalendarChartItem` to extract the label from.
   * @returns A string or number representing the label to be displayed.
   */
  getDisplayedLabel(item: CalendarChartItem): string | number {
    if (item.label) {
      return item.label;
    }

    if (item.date) {
      return new Date(item.date).getDate();
    }

    return '';
  }

  /**
   * Resolves the display color for a given calendar chart bar.
   *
   * Resolution priority:
   * 1. If the bar defines its own `color`, return it.
   * 2. If the bar has a `key` and it exists in the `legendColorMap`, return the mapped color.
   * 3. Otherwise, fall back to the global `data().colors?.filled` or the default filled color.
   *
   * @param bar - The `CalendarChartBar` for which the color is determined.
   * @returns A string representing the resolved color in HEX or CSS-compatible format.
   */
  getBarColor(bar: CalendarChartBar): string {
    if (bar.color) {
      return bar.color;
    }

    if (bar.key) {
      const legendColor = this.legendColorMap().get(bar.key);
      if (legendColor) {
        return legendColor;
      }
    }

    return this.data().colors?.filled ?? this.defaultColors.filled;
  }

  getBarHeightInPixels(calendarChartBar: CalendarChartBar): number {
    // When the chart is empty, we display a 8px x 8px filled circle
    if (this.isBarEmpty(calendarChartBar)) {
      return 8;
    }
    // When the 'heightPercentage' is specified, we calculate it from the max height
    if (calendarChartBar.heightPercentage) {
      return (
        (this.maxBarHeightInPixels() / 100) * calendarChartBar.heightPercentage
      );
    } else {
      // When the 'heightPercentage' is not specified, we take the max height
      return this.maxBarHeightInPixels();
    }
  }

  /**
   * Determines whether a calendar chart bar should be treated as empty.
   *
   * A bar is considered empty when either of the following is true:
   * - Its `heightPercentage` is explicitly `0`.
   * - Both `filledPercentage` and `heightPercentage` are missing/falsy.
   *
   * Empty bars are rendered as a small placeholder dot instead of a full bar.
   *
   * @param calendarChartBar - The bar to evaluate.
   * @returns `true` if the bar has no meaningful filled/height data, otherwise `false`.
   */
  isBarEmpty(calendarChartBar: CalendarChartBar): boolean {
    if (calendarChartBar.heightPercentage === 0) {
      return true;
    }
    return (
      !calendarChartBar.filledPercentage && !calendarChartBar.heightPercentage
    );
  }

  /**
   *
   * @param item
   * @returns true, when all the bars are empty
   */
  isItemEmpty(item: CalendarChartItemV2): boolean {
    return item.bars.every(this.isBarEmpty);
  }

  /**
   * Extracts a bar's duration in minutes by parsing the first tooltip entry
   * that looks like a duration (e.g. "8h 12m", "45m", "0m"). Returns 0 when no
   * duration-like text is present.
   *
   * @param bar - The bar whose tooltip is inspected.
   * @returns The parsed duration in minutes, or 0 when none is found.
   */
  getBarDurationInMinutes(bar: CalendarChartBar): number {
    for (const tooltip of bar.tooltip ?? []) {
      if (!('text' in tooltip)) {
        continue;
      }
      const minutes = this.parseDurationToMinutes(tooltip.text);
      if (minutes !== null) {
        return minutes;
      }
    }
    return 0;
  }

  /**
   * Parses a duration string into minutes. Supports both English ("8h 12m",
   * "45m", "0m") and Hungarian ("2ó 36p", "45p", "0p") units.
   *
   * @param text - The text to parse.
   * @returns The duration in minutes, or `null` when the text contains no
   *          hour/minute component (e.g. a date label).
   */
  parseDurationToMinutes(text: string): number | null {
    // h / ó -> hours, m / p -> minutes
    const hoursMatch = text.match(/(\d+)\s*(?:h|ó)/);
    const minutesMatch = text.match(/(\d+)\s*(?:m|p)/);

    if (!hoursMatch && !minutesMatch) {
      return null;
    }

    const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
    const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
    return hours * 60 + minutes;
  }

  /**
   * Transforms a CalendarChartItem into CalendarChartItemV2.
   *
   * @param item - The CalendarChartItem to transform
   * @returns A CalendarChartItemV2 object with a single bar
   */
  transformCalendarChartItemToV2(item: CalendarChartItem): CalendarChartItemV2 {
    return {
      date: item.date,
      labelColor: item.labelColor,
      bars: [
        {
          color: item.color,
          filledPercentage: item.filledPercentage,
          heightPercentage: item.heightPercentage,
          tooltip: item.tooltip,
        },
      ],
    };
  }
}
